import React from 'react';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CloseIcon from '@mui/icons-material/Close';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import { Box, Button, IconButton, Typography } from '@mui/material';

export interface CatalogFooterProps {
  /** Selected rows that are active (archivable). */
  activeCount: number;
  /** Selected rows that are archived (restorable). */
  archivedCount: number;
  /** True while a verb is in flight — disables every control. */
  pending: boolean;
  /** The result-count line ("247 things · 21 archived" / "Showing X of Y"). */
  countText: string;
  onArchive: () => void;
  onRestore: () => void;
  onClearSelection: () => void;
}

/**
 * Permanent table footer — the DataGrid convention: selection actions on the
 * left (only while rows are selected), result count on the right. Always
 * rendered, so entering selection mode never shifts the layout. A mixed
 * selection shows BOTH verbs, each scoped to its own subset.
 */
export const CatalogFooter: React.FC<CatalogFooterProps> = ({
  activeCount,
  archivedCount,
  pending,
  countText,
  onArchive,
  onRestore,
  onClearSelection,
}) => {
  const total = activeCount + archivedCount;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        minHeight: 52,
        flexShrink: 0,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {total > 0 && (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {total} selected
          </Typography>
          {activeCount > 0 && (
            <Button
              size="small"
              variant="contained"
              startIcon={<ArchiveOutlinedIcon />}
              disabled={pending}
              onClick={onArchive}
            >
              Archive {activeCount}
            </Button>
          )}
          {archivedCount > 0 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<UnarchiveOutlinedIcon />}
              disabled={pending}
              onClick={onRestore}
            >
              Restore {archivedCount}
            </Button>
          )}
          <IconButton
            size="small"
            aria-label="Clear selection"
            disabled={pending}
            onClick={onClearSelection}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
        {countText}
      </Typography>
    </Box>
  );
};

CatalogFooter.displayName = 'CatalogFooter';
